import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { BASE_URL } from "../../../config";
import { getAuthHeaders } from "../../../services/useAuth";
import ProductSalesModal from "./ProductSalesModal";
import Loader from "../../../dist/Loader";
import CustomButton from "../../../pages/CustomButton";
import formatDate from "../../../utils/formatDate";

const yieldToBrowser = () => new Promise((resolve) => setTimeout(resolve, 50));
/* ─────────────────────────────────────────────
   Export to CSV/Excel helper
───────────────────────────────────────────── */
const exportToCSV = (data, month, year) => {
  if (!data || data.length === 0) return;

  const daysInMonth = new Date(year, month, 0).getDate();
  const weekdayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const getDayLabel = (day) => {
    const d = new Date(year, month - 1, day);
    return `${day}-${weekdayLabels[d.getDay()]}`;
  };

  // Build header row
  const staticHeaders = [
    "S.No",
    "Emp ID",
    "State",
    "Employee Name",
    "Designation",
    "City/HQ",
    "Division",
    "DOJ",
    "DOR",
    "Manager",
  ];

  const dayHeaders = Array.from({ length: daysInMonth }, (_, i) =>
    getDayLabel(i + 1),
  );
  const headers = [...staticHeaders, ...dayHeaders, "Total"];

  // Build data rows
  const rows = data.map((row, index) => {
    const staticCells = [
      index + 1,
      row.user_id || "-",
      row.state || row.state_name || "",
      row.employee_name ||
        `${row.first_name || ""} ${row.last_name || ""}`.trim() ||
        "",
      row.designation || row.job_title || "-",
      row.city_hq || row.hq || row.headquarter_name || "",
      row.division || row.division_name || "",
      formatDate(row.doj),
      formatDate(row.dor),
      row.manager || row.manager_name || "N/A",
    ];

    const dayCells = Array.from({ length: daysInMonth }, (_, i) => {
      const day = i + 1;
      return row.daily_sales && row.daily_sales[day]
        ? row.daily_sales[day].total_qty || 0
        : 0;
    });

    const total = row.net_total_qty || row.total_qty || 0;
    return [...staticCells, ...dayCells, total];
  });

  // Convert to CSV string
  const escape = (val) => {
    const str = String(val ?? "");
    return str.includes(",") || str.includes('"') || str.includes("\n")
      ? `"${str.replace(/"/g, '""')}"`
      : str;
  };

  const csvContent = [headers, ...rows]
    .map((r) => r.map(escape).join(","))
    .join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `Monthly_Count_${year}_${String(month).padStart(2, "0")}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/* ─────────────────────────────────────────────
   Print helper
───────────────────────────────────────────── */
const handlePrint = (tableRef) => {
  if (!tableRef.current) return;

  const tableHTML = tableRef.current.outerHTML;
  const printWindow = window.open("", "_blank", "width=1200,height=800");
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Monthly Count Report</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { font-family: Arial, sans-serif; font-size: 11px; padding: 12px; }
          h2 { text-align: center; margin-bottom: 12px; font-size: 14px; }
          table { border-collapse: collapse; width: 100%; }
          th, td {
            border: 1px solid #999;
            padding: 4px 6px;
            white-space: nowrap;
            text-align: center;
          }
          thead tr { background: #cfeefb !important; }
          tr:nth-child(even) td { background: #f7f6f3; }
          .sunday { background: #bbf7d0 !important; }
          @media print {
            @page { size: landscape; margin: 8mm; }
            body { font-size: 9px; }
          }
        </style>
      </head>
      <body>
        <h2>Monthly Count Report</h2>
        ${tableHTML}
        <script>
          window.onload = function() {
            window.print();
            setTimeout(() => window.close(), 500);
          };
        <\/script>
      </body>
    </html>
  `);
  printWindow.document.close();
};

/* ═══════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════ */
const MonthlyCount = () => {
  const [states, setStates] = useState([{ id: "All", name: "All" }]);
  const [divisions, setDivisions] = useState([]);
  const [selectedDivision, setSelectedDivision] = useState("All");
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedState, setSelectedState] = useState("All");
  const [tableData, setTableData] = useState([]);
  const [showContainers, setShowContainers] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totals, setTotals] = useState({ totalCount: 0, totalEmployees: 0 });

  // ── Fetch states on mount ──────────────────
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/states`, {
          headers: getAuthHeaders(),
        });
        const raw = res.data || [];
        const list = Array.isArray(raw) ? raw : raw.data || [];
        setStates([{ id: "All", name: "All" }, ...list]);
      } catch (err) {
        console.error(err);
        setError("Failed to load states");
      }
    };
    fetchStates();
  }, []);

  // ── Fetch divisions when state changes ─────
  useEffect(() => {
    if (!selectedState) {
      setDivisions([]);
      setSelectedDivision("All");
      return;
    }
    const fetchDivisions = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/api/divisions?state_id=${selectedState}`,
          { headers: getAuthHeaders() },
        );
        setDivisions([{ id: "All", name: "All" }, ...(res.data || [])]);
        setSelectedDivision("All");
      } catch (err) {
        console.error(err);
        setError("Failed to load divisions");
      }
    };
    fetchDivisions();
  }, [selectedState]);

  // ── GO button handler ──────────────────────
  const handleGoClick = async () => {
    // 1. Show loader FIRST, then yield so browser paints it
    setIsLoading(true);
    setError(null);
    setTableData([]);
    setTotals({ totalCount: 0, totalEmployees: 0 });

    // ✅ KEY FIX: yield to the browser so the loader overlay is painted
    await yieldToBrowser();

    try {
      const params = new URLSearchParams();

      if (selectedState && selectedState !== "All") {
        params.set("state_id", selectedState);
      }

      if (selectedDivision && selectedDivision !== "All") {
        const divisionName = divisions.find(
          (d) => String(d.id) === String(selectedDivision),
        )?.name;
        if (divisionName) params.set("division", divisionName);
      }

      if (selectedYear) params.set("year", selectedYear);
      if (selectedMonth)
        params.set("month", String(selectedMonth).padStart(2, "0"));

      const queryString = params.toString();
      const url = `${BASE_URL}/api/reports/employee-monthly-sales${
        queryString ? `?${queryString}` : ""
      }`;

      const res = await axios.get(url, { headers: getAuthHeaders() });

      const payload = res.data || {};
      let rows = [];

      if (Array.isArray(payload.data)) {
        rows = payload.data;
      } else if (Array.isArray(payload.employees)) {
        rows = payload.employees;
      } else if (payload.data && Array.isArray(payload.data.employees)) {
        rows = payload.data.employees;
      } else if (Array.isArray(payload)) {
        rows = payload;
      } else {
        const nested = Object.values(payload).find((v) => Array.isArray(v));
        if (nested) rows = nested;
      }

      const totalCount =
        payload.count ??
        payload.total_count ??
        payload.total ??
        rows.length ??
        0;

      const totalEmployees =
        payload.employees_count ?? payload.total_employees ?? rows.length;

      setTableData(rows || []);
      setTotals({
        totalCount: totalCount || 0,
        totalEmployees: totalEmployees || rows.length || 0,
      });
      setShowContainers(true);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch data");
      setTableData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const months = [
    { value: 1, label: "Jan" },
    { value: 2, label: "Feb" },
    { value: 3, label: "Mar" },
    { value: 4, label: "Apr" },
    { value: 5, label: "May" },
    { value: 6, label: "Jun" },
    { value: 7, label: "Jul" },
    { value: 8, label: "Aug" },
    { value: 9, label: "Sep" },
    { value: 10, label: "Oct" },
    { value: 11, label: "Nov" },
    { value: 12, label: "Dec" },
  ];
  const years = Array.from(
    { length: 5 },
    (_, i) => new Date().getFullYear() - i,
  );

  return (
    <div className="p-2 border-2 border-gray-300 rounded-lg bg-white">
      {/* ── Loader Overlay ── */}
      {isLoading && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40">
          <Loader onCancel={() => setIsLoading(false)} />
        </div>
      )}

      {/* ── Filter Panel ── */}
      <div className="p-4 border-2 border-gray-300 rounded-lg bg-gray-50">
        <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
          <span className="text-blue-700">Monthly </span>
          <span className="text-blue-500">Count</span>
        </h1>

        <div className="punchedAttendence-filter-row1">
          <label className="punchedAttendence-label">State</label>
          <select
            value={selectedState}
            onChange={(e) => setSelectedState(e.target.value)}
            className="punchedAttendence-dropdown"
          >
            <option value="">Select State</option>
            {states.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>

          <label className="punchedAttendence-label">Division</label>
          <select
            value={selectedDivision}
            onChange={(e) => setSelectedDivision(e.target.value)}
            className="punchedAttendence-dropdown"
            disabled={!selectedState}
          >
            {divisions.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </div>

        <div className="punchedAttendence-filter-row2">
          <label className="punchedAttendence-label">Month</label>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
            className="punchedAttendence-dropdown"
          >
            {months.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>

          <label className="punchedAttendence-label">Year</label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="punchedAttendence-dropdown"
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>

        {/* GO button */}
        <div className="flex justify-center mt-4">
          <CustomButton
            variant="go"
            onClick={handleGoClick}
            disabled={isLoading}
          >
            {isLoading ? "Loading..." : "GO"}
          </CustomButton>
        </div>
      </div>

      {/* ── Error ── */}
      {error && (
        <div className="mt-3 text-center text-red-600 font-medium">{error}</div>
      )}

      {/* ── Results Table ── */}
      {showContainers && (
        <div className="mt-4">
          {tableData.length > 0 ? (
            <MonthlyAttendanceTable
              data={tableData}
              month={selectedMonth}
              year={selectedYear}
            />
          ) : (
            <div className="text-center p-4 text-gray-500">
              No data available
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════
   TABLE SUB-COMPONENT
═══════════════════════════════════════════ */
const MonthlyAttendanceTable = ({ data, month, year }) => {
  const tableRef = useRef(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  const weekdayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const daysInMonth = new Date(year, month, 0).getDate();

  const getDayOfWeek = (y, m, d) => new Date(y, m - 1, d).getDay();

  const handleDayClick = (employeeId, day) => {
    setSelectedEmployee(employeeId);
    setSelectedDate(new Date(year, month - 1, day));
  };

  const closeModal = () => {
    setSelectedEmployee(null);
    setSelectedDate(null);
  };

  // ── Day header cells ──────────────────────
  const generateDayHeaders = () =>
    Array.from({ length: daysInMonth }, (_, i) => {
      const day = i + 1;
      const dow = getDayOfWeek(year, month, day);
      const isSunday = dow === 0;
      return (
        <th
          key={day}
          className={`text-center p-0.5 text-[11px] whitespace-nowrap w-[30px] ${
            isSunday ? "bg-green-200" : ""
          }`}
          scope="col"
        >
          <div>{day}</div>
          <div className="text-[9px]">{weekdayLabels[dow]}</div>
        </th>
      );
    });

  // ── Day data cells ────────────────────────
  const generateDayCells = (rowData) =>
    Array.from({ length: daysInMonth }, (_, i) => {
      const day = i + 1;
      const dow = getDayOfWeek(year, month, day);
      const isSunday = dow === 0;
      const dayValue =
        rowData.daily_sales && rowData.daily_sales[day]
          ? rowData.daily_sales[day].total_qty || 0
          : 0;

      return (
        <td
          key={day}
          className={`text-center p-0.5 font-bold text-xs whitespace-nowrap w-[30px] ${
            isSunday ? "bg-green-200" : ""
          } ${dayValue > 0 ? "cursor-pointer" : "cursor-default"}`}
          onClick={() => dayValue > 0 && handleDayClick(rowData.emp_id, day)}
        >
          <a
            href="#"
            onClick={(e) => e.preventDefault()}
            className={
              dayValue > 0 ? "text-blue-700 hover:underline" : "text-gray-400"
            }
          >
            {dayValue}
          </a>
        </td>
      );
    });

  return (
    <>
      {/* ── Table ── */}
      <div className="border rounded shadow table-wrapper">
        <table ref={tableRef} className="unlock-dcr-leave-table text-sm">
          <thead className="bg-blue-500 text-white">
            <tr className="bg-[#cfeefb]">
              {[
                ["S.No", "40px"],
                ["Emp ID", "80px"],
                ["State", "120px"],
                ["Employee Name", "180px"],
                ["Designation", "140px"],
                ["City/HQ", "120px"],
                ["Division", "120px"],
                ["DOJ", "90px"],
                ["DOR", "90px"],
                ["Manager", "180px"],
              ].map(([label, w]) => (
                <th
                  key={label}
                  style={{ width: w }}
                  className="text-center p-1.5 text-[13px] border border-gray-300"
                  scope="col"
                >
                  {label}
                </th>
              ))}

              {generateDayHeaders()}

              <th
                className="text-center p-1.5 text-[13px] w-[80px] border border-gray-300"
                scope="col"
              >
                Total
              </th>
            </tr>
          </thead>

          <tbody>
            {data.map((row, index) => (
              <tr
                key={index}
                className={`text-left text-[13px] ${
                  index % 2 === 0
                    ? "text-[#333333] bg-[#F7F6F3]"
                    : "text-[#284775] bg-white"
                }`}
              >
                <td className="text-center p-1.5 w-[40px] border border-gray-300">
                  {index + 1}
                </td>

                <td className="text-left p-1.5 w-[80px] border border-gray-300">
                  {row.user_id || "-"}
                </td>

                <td className="text-left p-1.5 w-[120px] border border-gray-300">
                  <span
                    title={row.state || row.state_name}
                    className="block overflow-hidden text-ellipsis whitespace-nowrap"
                  >
                    {row.state || row.state_name || ""}
                  </span>
                </td>

                <td className="text-left p-1.5 w-[180px] border border-gray-300">
                  <span
                    title={
                      row.employee_name ||
                      `${row.first_name || ""} ${row.last_name || ""}`.trim()
                    }
                    className="block overflow-hidden text-ellipsis whitespace-nowrap"
                  >
                    {row.employee_name ||
                      `${row.first_name || ""} ${row.last_name || ""}`.trim() ||
                      ""}
                  </span>
                </td>

                <td className="text-left p-1.5 w-[140px] border border-gray-300">
                  <span
                    title={row.designation || row.job_title}
                    className="block overflow-hidden text-ellipsis whitespace-nowrap"
                  >
                    {row.designation || row.job_title || "-"}
                  </span>
                </td>

                <td className="text-left p-1.5 w-[120px] border border-gray-300">
                  <span
                    title={row.city_hq || row.hq || row.headquarter_name}
                    className="block overflow-hidden text-ellipsis whitespace-nowrap"
                  >
                    {row.city_hq || row.hq || row.headquarter_name || ""}
                  </span>
                </td>

                <td className="text-left p-1.5 w-[120px] border border-gray-300">
                  <span
                    title={row.division || row.division_name}
                    className="block overflow-hidden text-ellipsis whitespace-nowrap"
                  >
                    {row.division || row.division_name || ""}
                  </span>
                </td>

                <td className="text-left p-1.5 w-[90px] border border-gray-300">
                  {formatDate(row.doj)}
                </td>

                <td className="text-left p-1.5 w-[90px] border border-gray-300">
                  {formatDate(row.dor)}
                </td>

                <td className="text-left p-1.5 w-[180px] border border-gray-300">
                  <span
                    title={row.manager || row.manager_name}
                    className="block overflow-hidden text-ellipsis whitespace-nowrap"
                  >
                    {row.manager || row.manager_name || "N/A"}
                  </span>
                </td>

                {generateDayCells(row)}

                <td className="text-center p-1.5 w-[80px] border border-gray-300">
                  <a
                    href="#"
                    onClick={(e) => e.preventDefault()}
                    className="text-blue-700 font-bold"
                  >
                    {row.net_total_qty || row.total_qty || 0}
                  </a>
                </td>
              </tr>
            ))}

            {/* Spacer footer row */}
            {data.length > 0 && (
              <tr className="text-center text-white bg-blue-500 font-bold">
                {Array.from({ length: 10 + daysInMonth + 1 }).map((_, i) => (
                  <td key={i} className="p-1.5 border border-gray-300">
                    &nbsp;
                  </td>
                ))}
              </tr>
            )}
          </tbody>
        </table>
        <div className="mt-4 flex gap-4 justify-center">
          <CustomButton
            onClick={() => handlePrint(tableRef)}
            label="Print"
            variant="print"
          >
            🖨
          </CustomButton>
          <CustomButton
            onClick={() => exportToCSV(data, month, year)}
            label="Export"
            variant="export"
          >
            📥
          </CustomButton>
        </div>
      </div>

      {/* ── Product Sales Modal ── */}
      {selectedEmployee && selectedDate && (
        <ProductSalesModal
          employeeId={selectedEmployee}
          date={selectedDate}
          onClose={closeModal}
        />
      )}
    </>
  );
};

export default MonthlyCount;
